import { IVideoSuggestionsRequest } from 'interfaces/i-video-options';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { HitterSide } from 'lib_ts/enums/hitters.enums';
import {
  DeliveryType,
  PitcherHand,
  PitchType,
} from 'lib_ts/enums/pitches.enums';
import { CameraPosition } from 'lib_ts/enums/videos.enums';
import { HasCreated, HasId } from 'lib_ts/interfaces/common/i-extensions';
import {
  IVideoQualityCriteria,
  IVideoSuggestionCriteria,
} from 'lib_ts/interfaces/i-video';
import { IMlbPlayerCoordinate } from 'lib_ts/interfaces/mlb-stats-api/i-player';

type SuggestionVideo = HasId &
  HasCreated &
  IVideoSuggestionCriteria &
  IVideoQualityCriteria;

const ENFORCE_RELEASE_SIDE = false;

// between 0-1, higher value makes matches by pitcher name stricter
const MIN_PERCENT_NAME_MATCHES = 0.51;

const SCORE_FACTOR = {
  // tie-breaking factors can be adjusted depending on which matter more than others
  pitchType: 50,
  delivery: 10,
  mlbSportId: 1,
  mlbSeason: 1,
  teamName: 1,
  hitterSide: 1,
};

export class VideoOptionsHelper {
  // calculates a relevance score (higher is more relevant) for the pitch x hitter vs video provided
  // negative value => video should not be suggested
  static getSuggestionScore(config: {
    video: SuggestionVideo;
    mlbPitcher: IMlbPlayerCoordinate | undefined;
    pitcherHand: PitcherHand | undefined;
    pitcherName: string | undefined;
    pitchType: PitchType | undefined;
    teamName: string | undefined;
    hitterSide: HitterSide | undefined;
    delivery: DeliveryType | undefined;
  }): number {
    const {
      video,
      mlbPitcher,
      pitcherName,
      pitcherHand,
      pitchType,
      teamName,
      hitterSide,
      delivery,
    } = config;

    // ! MANDATORY CHECKS - ensure the right handedness (disabled)
    if (
      ENFORCE_RELEASE_SIDE &&
      pitcherHand &&
      pitcherHand !== BallHelper.getPitcherHand(video.ReleaseSide)
    ) {
      // don't bother with checking anything else b/c of the side mismatch
      return -1;
    }

    // * MANDATORY CHECKS - get the right pitcher/person
    let isPlayerMatched = false;

    if (
      mlbPitcher &&
      video.mlb_pitcher &&
      mlbPitcher.playerPk === video.mlb_pitcher.playerPk
    ) {
      isPlayerMatched = true;
    }

    // from pitch or list
    if (pitcherName && video.PitcherFullName) {
      const nameParts = {
        pitch: MiscHelper.keyify(pitcherName),
        video: MiscHelper.keyify(video.PitcherFullName),
      };

      const maxParts = Math.max(nameParts.pitch.length, nameParts.video.length);

      const matchingParts = ArrayHelper.countIntersection(
        nameParts.pitch,
        nameParts.video
      );

      // check vs a min % over 50% so that things like "John Smith" don't count
      // as a match for "John Acorn" just because of one common token
      if (maxParts > 0 && matchingParts / maxParts > MIN_PERCENT_NAME_MATCHES) {
        // more matching parts => more confidence in match => higher score (pseudo tie-breaking)
        isPlayerMatched = true;
      }
    }

    if (!isPlayerMatched) {
      // if by now we don't have any mandatory factors matched, skip the rest
      return -1;
    }

    let score = 0;

    // * TIE BREAKING CHECKS BELOW

    if (mlbPitcher && video.mlb_pitcher) {
      if (mlbPitcher.sportId === video.mlb_pitcher.sportId) {
        score += SCORE_FACTOR.mlbSportId;
      }

      if (mlbPitcher.season === video.mlb_pitcher.season) {
        score += SCORE_FACTOR.mlbSeason;
      }
    }

    if (teamName && video.TeamName) {
      const nameParts = {
        pitch: MiscHelper.keyify(teamName),
        video: MiscHelper.keyify(video.TeamName),
      };

      const matchingParts = ArrayHelper.countIntersection(
        nameParts.pitch,
        nameParts.video
      );

      // more matching parts => more confidence in match => higher score
      score += matchingParts * SCORE_FACTOR.teamName;
    }

    if (pitchType === video.PitchType) {
      score += SCORE_FACTOR.pitchType;
    }

    if (delivery === video.DeliveryType) {
      score += SCORE_FACTOR.delivery;
    }

    if (video.CameraPosition) {
      switch (video.CameraPosition) {
        case CameraPosition.CEN: {
          score += SCORE_FACTOR.hitterSide;
          break;
        }

        default: {
          const isHitterLeft = hitterSide === HitterSide.LHH;
          const isCameraLeft = video.CameraPosition === CameraPosition.LHH;
          if (isHitterLeft === isCameraLeft) {
            score += SCORE_FACTOR.hitterSide * 2;
          }
          break;
        }
      }
    }

    return score;
  }

  // outputs a list of video IDs in descending relevance to the pitch x hitter combination provided
  static findSuggestions(config: {
    videos: SuggestionVideo[];
    request: IVideoSuggestionsRequest;
  }): string[] {
    const { request, videos } = config;
    const { pitch, list } = request;

    interface IShape {
      videoID: string;
      suggestionScore: number;

      // this needs to be separate so we can pair-wise compare videos
      // after verifying that they have the same suggestion score
      quality: {
        fps: number;
        resolution: number;
        uploaded: string;
      };
    }

    const mlbPitcher =
      pitch?.mlb_repertoire ?? list?.mlb_repertoire ?? undefined;

    const pitcherName = pitch?.pitcher ?? list?.pitcher ?? undefined;

    const teamName = pitch?.team ?? list?.team ?? undefined;

    const scoredIDs: IShape[] = videos
      .map((v) => {
        const o: IShape = {
          videoID: v._id,

          suggestionScore: this.getSuggestionScore({
            video: v,
            mlbPitcher: mlbPitcher,
            pitcherHand: BallHelper.getPitcherHand(pitch?.bs?.px),
            pitcherName: pitcherName,
            pitchType: pitch?.type,
            teamName: teamName,
            hitterSide: request.hitter?.side,
            delivery: request.delivery,
          }),

          quality: {
            fps: v.fps,
            // height * width + colors
            resolution: v.cap_size_0 * v.cap_size_1 + v.cap_size_2,
            uploaded: v._created,
          },
        };

        return o;
      })
      .filter(
        // don't suggest anything w/ a negative score
        (m) => m.suggestionScore >= 0
      );

    scoredIDs.sort((a, b) => {
      if (a.suggestionScore === b.suggestionScore) {
        // same suggestion scores, sort by highest quality first
        const { quality: qA } = a;
        const { quality: qB } = b;

        return (
          // higher fps first
          (qA.fps > qB.fps ? -1 : 1) +
          // higher resolution first
          (qA.resolution > qB.resolution ? -1 : 1) +
          // later date first
          -StringHelper.safeSort(qA.uploaded, qB.uploaded) // localeCompare sorts in ascending order => take -ve
        );
      }

      // different suggestion scores, sort by highest score first
      return a.suggestionScore > b.suggestionScore ? -1 : 1;
    });

    return scoredIDs.map((m) => m.videoID);
  }
}
